/* eslint-disable prefer-promise-reject-errors */
/* eslint-disable @typescript-eslint/naming-convention */
import useCloseReport from '@brands/Dashboard/ConsultationPage/hooks/useCloseReport';
import { isGumCase } from '@brands/Utils/isGumCase';
import { MedicalConditions, MedicalHistory } from '@brands/services/clinical_data/types/types';
import { ImageWithNotes } from '@brands/services/forms/types/ImageWithNotes';
import { getOneReportMedia } from '@brands/services/oneReport/getOneReportMedia';
import { IOneReportMedia } from '@brands/services/oneReport/types/IOneReportMedia';
import { getUserRoleName } from '@brands/Utils/getFriendlyName';
import * as html2pdf from 'html2pdf.js';
import moment from 'moment-timezone';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { IoClose, IoDownloadOutline } from 'react-icons/io5';
import { useParams } from 'react-router-dom';
import ReactToPrint from 'react-to-print';

import DefaultLogo from '../../../../default/Dashboard/Appointments/assets/oHRLogo.svg';
import GumLogo from '../../../../gum-virtual-care/Dashboard/Appointments/assets/oHRLogo.svg';
import Button from '../../../Components/Button/Button';
import { Loading } from '../../../Components/LoadingSpinner/Loading';
import { listClinicalData } from '../../../services/clinical_data/listClinicalData';
import { getOneCase } from '../../../services/cases/getCaseById';
import { defaultICaseValues } from '../../../services/cases/types/defaultICaseValues';
import { ICase } from '../../../services/cases/types/ICase';
import { getCaseDateForOHC } from '../../../Utils/formatDate';
import printer from '../../Appointments/assets/printer.svg';
import {
  formatOHCPhoneNumber,
  getOHCInsuranceCarrier,
  getOHCMedicalConditionsText,
  getOHCPharmacyLines,
  getSelectedOHCReportMedia,
  getVisibleOHCGoals,
  getVisibleOHCImages,
  getVisibleOHCNotes,
  hasOHCReportHtml,
  hasOHCText,
  isDisplayableOHCImage,
  sanitizeOHCReportHtml,
} from '../utils/oralHealthCoachingReport';
import styles from './styles.module.scss';

type ReportPageKey = 'details' | 'carePlan' | 'images' | 'notes';

const OralHealthCoachingReportComponent = (): JSX.Element => {
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState<ImageWithNotes[]>([]);
  const routeCaseId = useParams().caseId as string;
  const [thisCase, setCase] = useState<ICase>(defaultICaseValues);
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const currentLogo = isGumCase(thisCase) ? GumLogo : DefaultLogo;
  const componentRef = useRef<HTMLDivElement>(null);
  const [medicalHistory, setMedicalHistory] = useState<MedicalHistory | null>(null);
  const [medicalCondition, setMedicalCondition] = useState<MedicalConditions | null>(null);

  const goToPreviousPage = useCloseReport(thisCase.patient_id);

  const getCurrentCase = async (caseId: string): Promise<void> => {
    setLoading(true);
    try {
      const currentCase = await getOneCase(Number(caseId));
      setCase(currentCase);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getCurrentCase(routeCaseId);
  }, [routeCaseId]);

  const getFormAnswers = async (): Promise<void> => {
    const { medical_history, medical_conditions } = await listClinicalData(thisCase?.patient.id);
    setMedicalHistory(medical_history);
    setMedicalCondition(medical_conditions);
  };

  useEffect(() => {
    if (thisCase?.patient.id) {
      getFormAnswers();
    }
  }, [thisCase?.patient.id]);

  const loadImages = useCallback(async (): Promise<void> => {
    // Attachments on an Oral Health Coaching case live on its own report;
    // `one_report` is null for this case type, so reading only that left
    // this page's Images section permanently empty.
    const selectedMedia = getSelectedOHCReportMedia(
      thisCase?.oral_health_coaching_report?.media || thisCase?.one_report?.media,
    );

    if (!selectedMedia.length) {
      setImages([]);
      return;
    }

    const imageResults = await Promise.all(
      selectedMedia.map(async (mediaOneReport): Promise<ImageWithNotes | null> => {
        try {
          const mediaData = await getOneReportMedia(mediaOneReport.media_id!);

          if (!mediaData?.url || !isDisplayableOHCImage(mediaData.mimetype, mediaData.filename, mediaData.url)) {
            return null;
          }

          return {
            src: mediaData.url,
            width: 4,
            height: 3,
            filename: mediaData.filename,
            mimetype: mediaData.mimetype,
            notes: mediaOneReport.note,
            id: String(mediaOneReport.id),
            isSelected: mediaOneReport.is_selected,
            created_at: mediaOneReport.created_at,
          };
        } catch (error) {
          console.error(`Failed to load OHC report media ${mediaOneReport.media_id}:`, error);
          return null;
        }
      }),
    );

    setImages(imageResults.filter((image): image is ImageWithNotes => image !== null));
  }, [thisCase?.oral_health_coaching_report?.media, thisCase?.one_report?.media]);

  useEffect(() => {
    if (thisCase?.oral_health_coaching_report?.id || thisCase?.one_report?.id) {
      loadImages();
    }
  }, [loadImages, thisCase?.oral_health_coaching_report?.id, thisCase?.one_report?.id]);

  const convertSvgToPngDataUrl = async (svgUrl: string): Promise<string> => {
    const response = await fetch(svgUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch SVG: ${response.statusText}`);
    }
    const svgText = await response.text();

    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
    const svgElement = svgDoc.documentElement;

    let width = parseFloat(svgElement.getAttribute('width') || '0');
    let height = parseFloat(svgElement.getAttribute('height') || '0');

    const viewBox = svgElement.getAttribute('viewBox');
    if (viewBox) {
      const [, , vbWidth, vbHeight] = viewBox.split(/\s+/).map(Number);
      if (!width || !height || isNaN(width) || isNaN(height)) {
        width = vbWidth;
        height = vbHeight;
      }
    }

    if (!width || !height || isNaN(width) || isNaN(height)) {
      width = 300;
      height = 300;
    }

    svgElement.setAttribute('width', `${width}`);
    svgElement.setAttribute('height', `${height}`);

    const modifiedSvgText = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([modifiedSvgText], { type: 'image/svg+xml' });
    const blobUrl = URL.createObjectURL(svgBlob);

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        const aspectRatio = width / height;
        const targetWidth = 300;
        const targetHeight = targetWidth / aspectRatio;

        const scale = 2;
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth * scale;
        canvas.height = targetHeight * scale;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject('Canvas context not available');
          return;
        }

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const pngUrl = canvas.toDataURL('image/png');
        URL.revokeObjectURL(blobUrl);
        resolve(pngUrl);
      };

      img.onerror = (err) => {
        URL.revokeObjectURL(blobUrl);
        reject(new Error(`Failed to load SVG image: ${err}`));
      };

      img.src = blobUrl;
    });
  };

  const downloadModal = async (): Promise<void> => {
    const logoImages = Array.from(document.querySelectorAll<HTMLImageElement>('[data-ohc-report-logo="true"]'));

    await Promise.all(
      logoImages.map((logoImg) =>
        logoImg.src && logoImg.src.endsWith('.svg')
          ? convertSvgToPngDataUrl(logoImg.src)
              .then((pngDataUrl) => {
                logoImg.src = pngDataUrl;
              })
              .catch((err) => {
                console.error('Failed to convert report logo SVG to PNG:', err);
              })
          : Promise.resolve(),
      ),
    );

    if (componentRef?.current) {
      const element = componentRef.current as HTMLElement;

      element.querySelectorAll('.no-page-break').forEach((el) => {
        (el as HTMLElement).style.pageBreakInside = 'avoid';
        (el as HTMLElement).style.breakInside = 'avoid';
      });

      const options = {
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'pt', format: 'letter', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'] },
      };

      let newTab: Window | null = null;
      if (isMobile) newTab = window.open('about:blank', '_blank');

      try {
        const pdfBlob = await html2pdf().set(options).from(element).toPdf().output('blob');
        const pdfUrl = URL.createObjectURL(pdfBlob);

        if (isMobile && newTab) {
          newTab.location.href = pdfUrl;
        } else {
          const a = document.createElement('a');
          a.href = pdfUrl;
          a.download = 'OralHealthCoachingReport.pdf';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }

        setTimeout(() => URL.revokeObjectURL(pdfUrl), 10000);
      } catch (err) {
        console.error('Failed to generate PDF:', err);
      }
    } else {
      console.error('Component reference not available');
    }
  };

  const formatReportDateTime = (dateString?: string | null): string => {
    if (!dateString) return '';
    const date = moment.utc(dateString).tz(timeZone);
    return date.isValid() ? date.format('MMMM D, YYYY hh:mma') : '';
  };

  const formatDateOfBirth = (dateString?: string | null): string => {
    if (!dateString) return '';
    const date = moment(dateString);
    return date.isValid() ? date.format('MM/DD/YYYY') : dateString;
  };

  const formatNoteDate = (dateString?: string | null): string => {
    if (!dateString) return '';
    const date = moment.utc(dateString).tz(timeZone);
    return date.isValid() ? date.format('MM/DD/YYYY [at] hh:mma') : '';
  };

  const reportMedicalHistory = thisCase.medical_history_snapshot || medicalHistory;
  const reportMedicalConditions = thisCase.medical_conditions_snapshot || medicalCondition;
  const insuranceCarrier = getOHCInsuranceCarrier(thisCase);
  const conditionsText = getOHCMedicalConditionsText(reportMedicalConditions);
  const pharmacyLines = getOHCPharmacyLines(reportMedicalHistory);
  const consultationSummary = thisCase?.oral_health_coaching_report?.care_plan?.consultation_summary || '';
  const hasConsultationSummary = hasOHCReportHtml(consultationSummary);
  const visibleGoals = getVisibleOHCGoals(thisCase?.oral_health_coaching_report?.care_plan?.goals);
  const visibleImages = getVisibleOHCImages(images);
  const visibleNotes = getVisibleOHCNotes(thisCase?.one_report?.notes);
  const hasCarePlanPage = hasConsultationSummary || visibleGoals.length > 0;

  const reportPages = useMemo<ReportPageKey[]>(() => {
    const pages: ReportPageKey[] = ['details'];
    if (hasCarePlanPage) pages.push('carePlan');
    if (visibleImages.length > 0) pages.push('images');
    if (visibleNotes.length > 0) pages.push('notes');
    return pages;
  }, [hasCarePlanPage, visibleImages.length, visibleNotes.length]);

  if (!thisCase || loading) return <Loading fullScreen />;

  const renderHeader = (): JSX.Element => (
    <div className={styles.reportHeader}>
      <div
        className={`${styles.reportLogoWrap} ${
          thisCase.organization.uses_custom_logo ? styles.poweredByTextParent : ''
        }`}
      >
        <img
          crossOrigin="anonymous"
          src={currentLogo}
          alt="Dentistry.One"
          data-ohc-report-logo="true"
          className={`${styles.reportLogo} ${isGumCase(thisCase) ? styles.gumLogoCorner : ''}`}
        />
        {thisCase.organization.uses_custom_logo && (
          <span className={styles.poweredByText}>Powered by Dentistry.One</span>
        )}
      </div>
      <div className={styles.reportHeaderDetails}>
        <span>Oral Health Coaching Report</span>
        <span>{getCaseDateForOHC(thisCase.completed_at, timeZone)}</span>
      </div>
    </div>
  );

  const renderPage = (pageKey: ReportPageKey, pageNumber: number): JSX.Element => (
    <div className={styles.reportPage} key={pageKey}>
      {renderHeader()}
      <div className={styles.reportPageContent}>{renderPageContent(pageKey)}</div>
      <div className={styles.reportFooter}>
        Page {pageNumber} of {reportPages.length}
      </div>
    </div>
  );

  const renderSection = (title: string, children: React.ReactNode): JSX.Element => (
    <section className={`${styles.reportSection} no-page-break`}>
      <h2>{title}</h2>
      <div className={styles.sectionDivider} />
      {children}
    </section>
  );

  const renderField = (label: string, value?: React.ReactNode, className?: string): JSX.Element | null => {
    if (value === undefined || value === null || value === '') return null;

    return (
      <div className={`${styles.reportField} ${className || ''}`}>
        <span className={styles.reportFieldLabel}>{label}</span>
        <span className={styles.reportFieldValue}>{value}</span>
      </div>
    );
  };

  const renderPageContent = (pageKey: ReportPageKey): JSX.Element | null => {
    switch (pageKey) {
      case 'details':
        return (
          <>
            {renderSection(
              'Care team',
              <div className={styles.twoColumnGrid}>
                {renderField(
                  'Care Advisor',
                  `${thisCase.care_advisor.full_name}${
                    thisCase?.care_advisor.status === 'Deactivated' ? ' (Deactivated)' : ''
                  }`,
                )}
                {renderField(
                  'Collector',
                  thisCase.collector
                    ? `${thisCase.collector.full_name}${
                        thisCase.collector.status === 'Deactivated' ? ' (Deactivated)' : ''
                      }`
                    : undefined,
                )}
              </div>,
            )}

            {renderSection(
              'Appointment details',
              <div className={styles.twoColumnGrid}>
                {renderField(
                  'Video consultation',
                  formatReportDateTime(thisCase.video_conference_date || thisCase.created_at),
                )}
                {renderField('Consult type', 'Oral Health Coaching')}
                {renderField('Service requested', formatReportDateTime(thisCase.created_at))}
                {renderField('Report completed', formatReportDateTime(thisCase.completed_at))}
              </div>,
            )}

            {renderSection(
              'Patient details',
              <div className={styles.twoColumnGrid}>
                <div className={styles.reportFieldColumn}>
                  {renderField(
                    'Patient name',
                    `${thisCase.patient.full_name}${
                      thisCase?.patient.status === 'Deactivated' ? ' (Deactivated)' : ''
                    }`,
                  )}
                  {renderField('DOB', formatDateOfBirth(thisCase.patient.basic_info.date_of_birth))}
                  {renderField('Insurance', insuranceCarrier)}
                  {renderField('Email', thisCase.patient.email)}
                  {renderField('Phone number', formatOHCPhoneNumber(thisCase.patient.phone_number))}
                </div>
                <div className={styles.reportFieldColumn}>
                  {renderField('Conditions', conditionsText)}
                  {renderField('Medications', reportMedicalHistory?.medications)}
                  {renderField('Allergies', reportMedicalHistory?.medications_allergies)}
                  {pharmacyLines.length > 0 &&
                    renderField(
                      'Pharmacy',
                      <span className={styles.reportValueStack}>
                        {pharmacyLines.map((line) => (
                          <span key={line}>{line}</span>
                        ))}
                      </span>,
                    )}
                </div>
              </div>,
            )}
          </>
        );
      case 'carePlan':
        return renderSection(
          'Your care plan',
          <div className={styles.carePlanContent}>
            {hasConsultationSummary && (
              <div className={`${styles.carePlanBlock} no-page-break`}>
                <span className={styles.reportFieldLabel}>Summary</span>
                <div
                  className={`${styles.richText} fs-exclude`}
                  dangerouslySetInnerHTML={{ __html: sanitizeOHCReportHtml(consultationSummary) }}
                />
              </div>
            )}

            {visibleGoals.map((goal, index) => (
              <div className={`${styles.goalBlock} no-page-break`} key={goal.id || index}>
                <span className={styles.reportFieldLabel}>{`Goal ${index + 1}`}</span>
                <div
                  className={`${styles.richText} fs-exclude`}
                  dangerouslySetInnerHTML={{ __html: sanitizeOHCReportHtml(goal.goal_text || '') }}
                />

                {goal.educations.map((education) => (
                  <div className={styles.educationResource} key={education.id || `${education.title}-${education.url}`}>
                    {hasOHCText(education.title) && <span className={styles.reportFieldLabel}>{education.title}</span>}
                    {hasOHCText(education.url) ? (
                      <a href={education.url} target="_blank" rel="noopener noreferrer">
                        {education.url}
                      </a>
                    ) : null}
                  </div>
                ))}
              </div>
            ))}
          </div>,
        );
      case 'images':
        return renderSection(
          'Images',
          <div className={styles.imagesGrid}>
            {visibleImages.map((image, index) => (
              <div className={`${styles.imageItem} no-page-break`} key={image.id || image.src || index}>
                <div className={styles.imageFrame}>
                  <img crossOrigin="anonymous" src={image.src} alt="" />
                </div>
                {hasOHCText(image.notes) && <div className={styles.imageCaption}>{image.notes}</div>}
              </div>
            ))}
          </div>,
        );
      case 'notes':
        return renderSection(
          'Notes',
          <div className={styles.notesList}>
            {visibleNotes.map((note) => (
              <div className={`${styles.noteItem} no-page-break`} key={note.id}>
                <div className={styles.noteMeta}>
                  <span>{formatNoteDate(note.last_edited_at || note.created_at)}</span>
                  <span>|</span>
                  <span>
                    {note.created_by.full_name} ({getUserRoleName(note.created_by.role.name)})
                  </span>
                </div>
                <div
                  className={`${styles.richText} fs-exclude`}
                  dangerouslySetInnerHTML={{ __html: sanitizeOHCReportHtml(note.note) }}
                />
              </div>
            ))}
          </div>,
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.modalBackground}>
      <div>
        <div className={`fs-unmask ${styles.modalCloseBtn}`} onClick={goToPreviousPage}>
          <span>Close</span>
          <div className={styles.circle}>
            <IoClose />
          </div>
        </div>
        <ReactToPrint
          trigger={() => (
            <Button type="button" className={styles.print}>
              <img src={printer} alt="Print" />
              Print
            </Button>
          )}
          content={() => componentRef.current}
        />
        <div className={styles.download}>
          <Button type="button" className={styles.print} onClick={downloadModal}>
            <IoDownloadOutline />
            Download
          </Button>
        </div>
      </div>
      <div className={styles.modalDiv}>
        <div className={styles.modalContainer}>
          <div className={styles.printableDiv} ref={componentRef}>
            {reportPages.map((pageKey, index) => renderPage(pageKey, index + 1))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OralHealthCoachingReportComponent;
